import { ReactNode, useEffect } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";

interface ModalProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  subtitle?: string;
  children: ReactNode;
  maxWidth?: string;
}

export default function Modal({ open, onClose, title, subtitle, children, maxWidth = "max-w-md" }: ModalProps) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => { window.removeEventListener("keydown", onKey); document.body.style.overflow = ""; };
  }, [open, onClose]);

  if (!open) return null;

  // Portal til <body> så modalen aldri fanges av en transformert forelder
  // (f.eks. animate-fade-up), som ellers bryter position:fixed.
  return createPortal(
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" onClick={onClose}>
      <div className="absolute inset-0 bg-black/50 backdrop-blur-[2px] animate-backdrop-in" />
      <div
        onClick={(e) => e.stopPropagation()}
        className={`relative w-full ${maxWidth} bg-card rounded-[16px] shadow-pop border border-line animate-fade-up max-h-[90vh] overflow-y-auto`}
      >
        {(title || subtitle) && (
          <div className="flex items-start justify-between gap-4 px-5 pt-5 pb-3 sticky top-0 bg-card z-10">
            <div className="min-w-0">
              {title && <h2 className="text-[18px] font-bold text-night">{title}</h2>}
              {subtitle && <p className="text-[13px] text-muted mt-0.5">{subtitle}</p>}
            </div>
            <button onClick={onClose} className="p-1.5 -mr-1 -mt-1 rounded-[9px] text-faint hover:text-night hover:bg-black/[0.05] transition-colors shrink-0">
              <X className="w-4.5 h-4.5" />
            </button>
          </div>
        )}
        <div className="px-5 pb-5">{children}</div>
      </div>
    </div>,
    document.body
  );
}
